/**
 * Admin Tier Management Page
 *
 * CRUD interface for managing subscription tiers.
 * Tiers are grouped by type (broker vs company).
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Star,
  Eye,
  EyeOff,
  X,
  ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import Button from '../../../../components/base/Button';
import adminService, {
  type SubscriptionTier,
  type CreateTierParams,
  type Addon,
  type DiscountKind,
} from '../../../../services/adminService';

const DISCOUNT_KINDS: DiscountKind[] = ['NONE', 'PERCENTAGE', 'FIXED_SAR', 'COINS'];

// ============================================================================
// TIER FORM MODAL
// ============================================================================

interface TierAddonState {
  addonId: string;
  quantity: string;
}

function TierFormModal({
  tier,
  addons,
  onSave,
  onClose,
  isLoading,
}: {
  tier?: SubscriptionTier;
  addons: Addon[];
  onSave: (data: CreateTierParams) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const { t, i18n } = useTranslation('bundles');
  const isRTL = i18n.language === 'ar';
  const isEdit = !!tier;

  const localizedAddon = useCallback(
    (a: Pick<Addon, 'name' | 'nameAr'>) => (isRTL ? a.nameAr || a.name : a.name),
    [isRTL],
  );

  const [formData, setFormData] = useState<CreateTierParams>({
    name: tier?.name || '',
    nameAr: tier?.nameAr || '',
    slug: tier?.slug || '',
    description: tier?.description || '',
    descriptionAr: tier?.descriptionAr || '',
    priceMonthly: tier?.priceMonthly || 0,
    priceYearly: tier?.priceYearly || 0,
    planType: (tier?.planType as 'broker' | 'company') || 'broker',
    isPopular: tier?.isPopular || false,
    sortOrder: tier?.sortOrder || 0,
    maxListings: tier?.maxListings || 10,
    maxSubUsers: tier?.maxSubUsers || 0,
    includedCoins: tier?.includedCoins ?? 0,
    features: tier?.features || {},
    isCustom: tier?.isCustom || false,
    discountKind: tier?.discountKind || 'NONE',
    discountValue: tier?.discountValue ?? 0,
  });

  // Embedded add-ons editor state (kept as strings for the inputs).
  const [tierAddons, setTierAddons] = useState<TierAddonState[]>(
    tier?.addons?.map((a) => ({ addonId: a.addonId, quantity: String(a.quantity || 1) })) ?? [],
  );

  const addTierAddon = () => {
    const used = new Set(tierAddons.map((a) => a.addonId));
    const next = addons.find((a) => !used.has(a.id));
    if (!next) return;
    setTierAddons((prev) => [...prev, { addonId: next.id, quantity: '1' }]);
  };

  const updateTierAddon = (index: number, patch: Partial<TierAddonState>) => {
    setTierAddons((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const removeTierAddon = (index: number) => {
    setTierAddons((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      discountValue: formData.discountKind === 'NONE' ? 0 : formData.discountValue,
      addons: tierAddons.map((a) => ({
        addonId: a.addonId,
        quantity: Number(a.quantity) || 1,
      })),
    });
  };

  const discountValueHint =
    formData.discountKind === 'PERCENTAGE'
      ? t('admin.fieldDiscountValueHintPercentage')
      : formData.discountKind === 'FIXED_SAR'
        ? t('admin.fieldDiscountValueHintFixed')
        : formData.discountKind === 'COINS'
          ? t('admin.fieldDiscountValueHintCoins')
          : '';

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">{isEdit ? 'Edit Tier' : 'Create New Tier'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name (English)</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    name: e.target.value,
                    slug: isEdit ? formData.slug : generateSlug(e.target.value),
                  });
                }}
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Name (Arabic)</label>
              <input
                type="text"
                value={formData.nameAr}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                dir="rtl"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Slug</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border font-mono dark:bg-gray-700 dark:border-gray-600"
              required
              disabled={isEdit}
            />
            <p className="text-xs text-gray-500 mt-1">
              Used in URLs and API. Cannot be changed after creation.
            </p>
          </div>

          {/* Plan Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Plan Type</label>
            <select
              value={formData.planType}
              onChange={(e) =>
                setFormData({ ...formData, planType: e.target.value as 'broker' | 'company' })
              }
              className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
            >
              <option value="broker">Broker</option>
              <option value="company">Company</option>
            </select>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Monthly Price (SAR)</label>
              <input
                type="number"
                value={formData.priceMonthly}
                onChange={(e) =>
                  setFormData({ ...formData, priceMonthly: parseInt(e.target.value) || 0 })
                }
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                min="0"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Yearly Price (SAR)</label>
              <input
                type="number"
                value={formData.priceYearly}
                onChange={(e) =>
                  setFormData({ ...formData, priceYearly: parseInt(e.target.value) || 0 })
                }
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                min="0"
                required
              />
            </div>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Max Listings</label>
              <input
                type="number"
                value={formData.maxListings}
                onChange={(e) =>
                  setFormData({ ...formData, maxListings: parseInt(e.target.value) || 0 })
                }
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Team Members</label>
              <input
                type="number"
                value={formData.maxSubUsers}
                onChange={(e) =>
                  setFormData({ ...formData, maxSubUsers: parseInt(e.target.value) || 0 })
                }
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">Only applicable for company plans</p>
            </div>
          </div>

          {/* Included coins */}
          <div>
            <label className="block text-sm font-medium mb-2">Included Coins</label>
            <input
              type="number"
              value={formData.includedCoins}
              onChange={(e) =>
                setFormData({ ...formData, includedCoins: parseInt(e.target.value) || 0 })
              }
              className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Coins granted to the user when they subscribe to this plan.
            </p>
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPopular}
                onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
                className="w-4 h-4 rounded text-[#004D40]"
              />
              <span className="text-sm">Mark as Popular</span>
            </label>

            <div className="flex items-center gap-2">
              <label className="text-sm">Sort Order:</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) =>
                  setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })
                }
                className="w-20 px-2 py-1 rounded border dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border resize-none dark:bg-gray-700 dark:border-gray-600"
              rows={2}
            />
          </div>

          {/* Custom plan toggle */}
          <div className="pt-2 border-t dark:border-gray-700">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isCustom ?? false}
                onChange={(e) => setFormData({ ...formData, isCustom: e.target.checked })}
                className="w-4 h-4 mt-0.5 rounded text-[#004D40]"
              />
              <span>
                <span className="text-sm font-medium">{t('plan.isCustom')}</span>
                <span className="block text-xs text-gray-500">{t('plan.isCustomHint')}</span>
              </span>
            </label>
          </div>

          {/* Discount picker (segmented) + value */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('plan.discount')}</label>
            <div className="flex flex-wrap gap-2">
              {DISCOUNT_KINDS.map((kind) => {
                const active = (formData.discountKind ?? 'NONE') === kind;
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setFormData({ ...formData, discountKind: kind })}
                    className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                      active
                        ? 'bg-[#004D40] border-[#004D40] text-white'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t(`discountKind.${kind}`)}
                  </button>
                );
              })}
            </div>
            {(formData.discountKind ?? 'NONE') !== 'NONE' && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-2">{t('plan.discountValue')}</label>
                <input
                  type="number"
                  value={formData.discountValue ?? 0}
                  onChange={(e) =>
                    setFormData({ ...formData, discountValue: parseInt(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  min="0"
                  max={formData.discountKind === 'PERCENTAGE' ? '100' : undefined}
                />
                {discountValueHint && (
                  <p className="text-xs text-gray-500 mt-1">{discountValueHint}</p>
                )}
              </div>
            )}
          </div>

          {/* Embedded add-ons */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">{t('plan.embeddedAddons')}</label>
              <Button
                size="sm"
                variant="outline"
                onClick={addTierAddon}
                disabled={addons.length === 0 || tierAddons.length >= addons.length}
              >
                <Plus className="w-3.5 h-3.5" />
                {t('admin.addItem')}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-3">{t('plan.embeddedAddonsHint')}</p>

            {addons.length === 0 ? (
              <p className="text-sm text-gray-500">{t('admin.noAddonsAvailable')}</p>
            ) : tierAddons.length === 0 ? (
              <p className="text-sm text-gray-500">-</p>
            ) : (
              <div className="space-y-2">
                {tierAddons.map((item, index) => {
                  const usedElsewhere = new Set(
                    tierAddons.filter((_, i) => i !== index).map((i) => i.addonId),
                  );
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 rounded-lg border dark:border-gray-600"
                    >
                      <select
                        value={item.addonId}
                        onChange={(e) => updateTierAddon(index, { addonId: e.target.value })}
                        className="flex-1 px-3 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        {addons.map((a) => (
                          <option key={a.id} value={a.id} disabled={usedElsewhere.has(a.id)}>
                            {localizedAddon(a)}
                            {a.priceSar != null
                              ? `${t('priceSar', { price: a.priceSar.toLocaleString() })}`
                              : ''}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">{t('admin.quantity')}</span>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateTierAddon(index, { quantity: e.target.value })}
                          className="w-16 px-2 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          min="1"
                          max="100"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTierAddon(index)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg"
                        title={t('admin.remove')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : isEdit ? (
                'Update Tier'
              ) : (
                'Create Tier'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AdminTiersPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  // Add-ons available to embed in custom plans (priced, non-quote).
  const [addons, setAddons] = useState<Addon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingTier, setEditingTier] = useState<SubscriptionTier | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch tiers + the embeddable add-on catalog.
  const fetchTiers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [tierData, addonData] = await Promise.all([
        adminService.getTiers(),
        adminService.getAddons().catch(() => [] as Addon[]),
      ]);
      setTiers(tierData);
      // Exclude quote-based add-ons — they have no flat price to embed.
      setAddons(addonData.filter((a) => !a.isQuoteBased && a.priceSar != null));
    } catch (err) {
      console.error('Failed to fetch tiers:', err);
      setError('Failed to load subscription tiers');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  // Create tier
  const handleCreate = async (data: CreateTierParams) => {
    try {
      setIsSaving(true);
      setError(null);
      await adminService.createTier(data);
      setShowCreateModal(false);
      fetchTiers();
    } catch (err: unknown) {
      console.error('Failed to create tier:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to create tier';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Update tier
  const handleUpdate = async (data: CreateTierParams) => {
    if (!editingTier) return;
    try {
      setIsSaving(true);
      setError(null);
      await adminService.updateTier(editingTier.id, data);
      setEditingTier(null);
      fetchTiers();
    } catch (err: unknown) {
      console.error('Failed to update tier:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to update tier';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete tier
  const handleDelete = async (tier: SubscriptionTier) => {
    if (!confirm(`Are you sure you want to deactivate "${tier.name}"?`)) return;
    try {
      setError(null);
      await adminService.deleteTier(tier.id);
      fetchTiers();
    } catch (err) {
      console.error('Failed to delete tier:', err);
      setError('Failed to deactivate tier');
    }
  };

  // Group tiers by type
  const brokerTiers = tiers.filter((t) => t.planType === 'broker' || t.planType === 'agent');
  const companyTiers = tiers.filter((t) => t.planType === 'company');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 p-8 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white">
                Subscription Tiers
              </h1>
              <p className="text-gray-500">Manage subscription plans for brokers and companies</p>
            </div>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Tier
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {/* Broker Tiers */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 text-[var(--pv-color-primary)] dark:text-white">
            Broker Plans
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Monthly
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Yearly
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Listings
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-gray-700">
                {brokerTiers.map((tier) => (
                  <tr
                    key={tier.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {tier.name}
                        </span>
                        {tier.isPopular && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{tier.slug}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {tier.priceMonthly.toLocaleString()} SAR
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {tier.priceYearly.toLocaleString()} SAR
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {tier.maxListings}
                    </td>
                    <td className="px-4 py-3">
                      {tier.isActive ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <Eye className="w-4 h-4" /> Active
                        </span>
                      ) : (
                        <span className="text-gray-400 flex items-center gap-1">
                          <EyeOff className="w-4 h-4" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setEditingTier(tier)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg mr-1"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(tier)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg"
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {brokerTiers.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No broker plans yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Company Tiers */}
        <div>
          <h2 className="text-lg font-semibold mb-4 text-[var(--pv-color-primary)] dark:text-white">
            Company Plans
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Monthly
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Yearly
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Listings
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Team
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-gray-700">
                {companyTiers.map((tier) => (
                  <tr
                    key={tier.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {tier.name}
                        </span>
                        {tier.isPopular && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{tier.slug}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {tier.priceMonthly.toLocaleString()} SAR
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {tier.priceYearly.toLocaleString()} SAR
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {tier.maxListings}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {tier.maxSubUsers}
                    </td>
                    <td className="px-4 py-3">
                      {tier.isActive ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <Eye className="w-4 h-4" /> Active
                        </span>
                      ) : (
                        <span className="text-gray-400 flex items-center gap-1">
                          <EyeOff className="w-4 h-4" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setEditingTier(tier)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg mr-1"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={() => handleDelete(tier)}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 rounded-lg"
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {companyTiers.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No company plans yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <TierFormModal
          addons={addons}
          onSave={handleCreate}
          onClose={() => setShowCreateModal(false)}
          isLoading={isSaving}
        />
      )}

      {/* Edit Modal */}
      {editingTier && (
        <TierFormModal
          tier={editingTier}
          addons={addons}
          onSave={handleUpdate}
          onClose={() => setEditingTier(null)}
          isLoading={isSaving}
        />
      )}
    </div>
  );
}
